/**
 * @file AppTableSettings.tsx
 * @description Unified table settings panel with gear icon.
 * Controls column visibility and is extensible for future settings.
 * 
 * GOVERNED BY: copilot-instructions.md – Sections 7, 23 (UI Standards)
 * 
 * Features:
 * - Gear icon (⚙️) at table toolbar
 * - Column visibility toggles
 * - Persistent preferences per table
 * - Extensible design for future: density, export, column order
 */

import { memo, useCallback, useMemo, useEffect } from 'react';
import { Settings, RotateCcw, Columns3 } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../dropdown-menu';
import { Button } from '../button';
import { IconGhostButton } from '../button';
import { cn } from '@/lib/utils';
import type { AppTableColumn, VisibleColumnsState } from './appTableTypes';
import { getHideableColumns } from './appTableTypes';

// ─────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────

export interface AppTableSettingsProps<TRow> {
  /** Column definitions */
  columns: AppTableColumn<TRow>[];
  /** Current visibility state */
  visibleColumns: VisibleColumnsState;
  /** Callback when visibility changes */
  onVisibleColumnsChange: (columns: VisibleColumnsState) => void;
  /** Unique table key for persistence (e.g., "user-assignments") */
  tableKey: string;
  /** Reset to default visibility */
  onReset?: () => void;
  /** Minimum columns that must remain visible */
  minVisible?: number;
  /** Whether to auto-persist to localStorage */
  autoPersist?: boolean;
  /** Additional className */
  className?: string;
}

// ─────────────────────────────────────────────────────────────
// Storage Helpers
// ─────────────────────────────────────────────────────────────

function getSettingsStorageKey(tableKey: string): string {
  return `appTable:${tableKey}:settings`;
}

function loadSettings(tableKey: string): { columns?: VisibleColumnsState } | null {
  try {
    const stored = localStorage.getItem(getSettingsStorageKey(tableKey));
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function saveSettings(tableKey: string, settings: { columns: VisibleColumnsState }): void {
  try {
    localStorage.setItem(getSettingsStorageKey(tableKey), JSON.stringify(settings));
  } catch {
    // Silently fail
  }
}

// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

/**
 * AppTableSettings - Unified table settings panel.
 * 
 * @example
 * ```tsx
 * <AppTableSettings
 *   columns={columns}
 *   visibleColumns={visibleColumns}
 *   onVisibleColumnsChange={setVisibleColumns}
 *   tableKey="user-assignments"
 *   onReset={resetColumnVisibility}
 * />
 * ```
 */
export const AppTableSettings = memo(function AppTableSettings<TRow>({
  columns,
  visibleColumns,
  onVisibleColumnsChange,
  tableKey,
  onReset,
  minVisible = 1,
  autoPersist = true,
  className,
}: AppTableSettingsProps<TRow>) {
  // Get hideable columns only
  const hideableColumns = useMemo(
    () => getHideableColumns(columns),
    [columns]
  );

  // Count currently visible hideable columns
  const visibleCount = useMemo(() => {
    return hideableColumns.filter((col) => visibleColumns[String(col.key)] !== false).length;
  }, [hideableColumns, visibleColumns]);

  // Load persisted settings on mount
  useEffect(() => {
    if (!autoPersist || !tableKey) return;
    
    const saved = loadSettings(tableKey);
    if (saved?.columns) {
      onVisibleColumnsChange({ ...visibleColumns, ...saved.columns });
    }
    // Only run on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableKey]);

  // Persist settings when visibility changes
  useEffect(() => {
    if (!autoPersist || !tableKey) return;
    
    saveSettings(tableKey, { columns: visibleColumns });
  }, [visibleColumns, tableKey, autoPersist]);

  // Handle column toggle with minimum visible check
  const handleToggle = useCallback((columnKey: string, checked: boolean) => {
    // Prevent hiding if it would go below minimum
    if (!checked && visibleCount <= minVisible) {
      return;
    }

    onVisibleColumnsChange({
      ...visibleColumns,
      [columnKey]: checked,
    });
  }, [visibleColumns, visibleCount, minVisible, onVisibleColumnsChange]);

  // If no hideable columns, don't render
  if (hideableColumns.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <IconGhostButton
          icon={Settings}
          tooltip="Table settings"
          className={cn('h-8 w-8', className)}
        />
      </DropdownMenuTrigger>
      
      <DropdownMenuContent align="end" className="w-56">
        {/* ─────────────────────────────────────────────────────────────
            COLUMNS SECTION
        ───────────────────────────────────────────────────────────── */}
        <DropdownMenuGroup>
          <DropdownMenuLabel className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <Columns3 className="w-3 h-3" />
            Columns
          </DropdownMenuLabel>
          
          {hideableColumns.map((column) => {
            const key = String(column.key);
            const isVisible = visibleColumns[key] !== false;
            const isLastVisible = isVisible && visibleCount <= minVisible;

            return (
              <DropdownMenuCheckboxItem
                key={key}
                checked={isVisible}
                disabled={isLastVisible}
                onCheckedChange={(checked: boolean) => handleToggle(key, checked)}
                className="text-[12px]"
              >
                {column.header}
              </DropdownMenuCheckboxItem>
            );
          })}
        </DropdownMenuGroup>

        {/* ─────────────────────────────────────────────────────────────
            FUTURE SECTIONS (placeholder structure)
            
            Uncomment and implement when needed:
            
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-[11px] text-muted-foreground">
                Density
              </DropdownMenuLabel>
              <DropdownMenuRadioGroup value={density} onValueChange={setDensity}>
                <DropdownMenuRadioItem value="compact">Compact</DropdownMenuRadioItem>
                <DropdownMenuRadioItem value="comfortable">Comfortable</DropdownMenuRadioItem>
              </DropdownMenuRadioGroup>
            </DropdownMenuGroup>
            
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuLabel className="text-[11px] text-muted-foreground">
                Export
              </DropdownMenuLabel>
              <DropdownMenuItem onClick={handleExportCSV}>
                <Download className="w-3 h-3 mr-2" />
                Export CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleExportExcel}>
                <FileSpreadsheet className="w-3 h-3 mr-2" />
                Export Excel
              </DropdownMenuItem>
            </DropdownMenuGroup>
        ───────────────────────────────────────────────────────────── */}

        {/* Reset button */}
        {onReset && (
          <>
            <DropdownMenuSeparator />
            <Button
              variant="ghost"
              size="sm"
              onClick={onReset}
              className="w-full justify-start h-8 px-2 text-[12px] text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="w-3 h-3 mr-2" />
              Reset to default
            </Button>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}) as <TRow>(props: AppTableSettingsProps<TRow>) => React.ReactElement | null;

export default AppTableSettings;
